import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { IconButton, InputAdornment, TextField } from '@mui/material'
import { useEffect, useState } from 'react'

interface Props {
  label: string
  value: number
  onChange: (value: number) => void
  min: number
  max: number
  step: number
  helperText?: string
  disabled?: boolean
  /**
   * Caps how wide the field grows. It still fills what it is given below that,
   * so it shrinks on a narrow window -- a four-character value stretched across
   * a desktop panel just looks broken.
   */
  maxWidth?: number
}

/**
 * Rounds to the step, in a way that survives floating point.
 *
 * 0.1 + 0.05 is 0.15000000000000002, and three more presses make it visible in
 * the field. Counting whole steps from `min` and multiplying keeps the value on
 * the grid the caller asked for.
 */
function snap(value: number, min: number, max: number, step: number): number {
  const steps = Math.round((value - min) / step)
  const snapped = min + steps * step
  const clamped = Math.min(max, Math.max(min, snapped))
  // Two decimals is enough for a 0.05 grid and stops 0.15000000000000002 ever
  // reaching the input's value attribute.
  return Number(clamped.toFixed(2))
}

/**
 * A number field with explicit minus and plus buttons.
 *
 * The native spinners are a few pixels tall and effectively unusable with a
 * trackpad, which is the whole reason this exists. Typing is still allowed --
 * jumping from 0.8 to 0.3 should not take ten clicks -- and what is typed is
 * snapped to the step and clamped on blur rather than while typing, so a
 * half-finished "0." does not get rewritten under the cursor.
 */
export default function NumberStepper({
  label,
  value,
  onChange,
  min,
  max,
  step,
  helperText,
  disabled = false,
  maxWidth,
}: Props) {
  const [text, setText] = useState(() => value.toFixed(2))

  // Follow the value when it changes from outside, e.g. the buttons or a reset.
  useEffect(() => {
    setText(value.toFixed(2))
  }, [value])

  function commit(raw: string) {
    const parsed = Number(raw)
    const usable = raw.trim() !== '' && Number.isFinite(parsed)
    // Anything unparseable returns the field to the last good value rather than
    // clearing it: an empty strength would silently fall back to the GPU's own
    // default, which is not what the field was showing.
    const next = usable ? snap(parsed, min, max, step) : value
    onChange(next)
    setText(next.toFixed(2))
  }

  const atMin = value <= min
  const atMax = value >= max

  return (
    <TextField
      size="small"
      label={label}
      value={text}
      disabled={disabled}
      onChange={(event) => setText(event.target.value)}
      onBlur={(event) => commit(event.target.value)}
      onKeyDown={(event) => {
        // Handled here rather than left to a native number input: this is a text
        // field on purpose, so the browser's own spinners do not sit alongside
        // the buttons and offer a second, differently-behaved control.
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
        event.preventDefault()
        const direction = event.key === 'ArrowUp' ? 1 : -1
        onChange(snap(value + direction * step, min, max, step))
      }}
      helperText={helperText}
      fullWidth
      sx={{ maxWidth }}
      inputProps={{
        inputMode: 'decimal',
        style: { textAlign: 'center' },
        // Announced for assistive tech, which cannot see the buttons' range.
        role: 'spinbutton',
        'aria-valuemin': min,
        'aria-valuemax': max,
        'aria-valuenow': value,
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <IconButton
              size="small"
              aria-label={`Decrease ${label.toLowerCase()}`}
              disabled={disabled || atMin}
              onClick={() => onChange(snap(value - step, min, max, step))}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label={`Increase ${label.toLowerCase()}`}
              disabled={disabled || atMax}
              onClick={() => onChange(snap(value + step, min, max, step))}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  )
}
